import { useEffect, useState } from 'react';
import { UploadedFile } from '../../models/File';
import { store } from '../../redux/store';
import { invokeFastApi } from '../apis/fastapi';
import { logError } from '../utils';
import { useToaster } from './toast';

interface FilesResponse {
  files: UploadedFile[];
  loading: boolean;
  getFiles: () => Promise<void>;
  uploadFile: (file: File) => Promise<void>;
  uploading: boolean;
}

export const useFiles = (): FilesResponse => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const toaster = useToaster();

  const getFiles = async () => {
    setLoading(true);
    setFiles([]);
    try {
      const res = await invokeFastApi({
        path: '/document_uploads',
        method: 'GET',
      });

      const { documents } = (await res) as { documents: UploadedFile[] };
      setFiles(documents);
    } catch (fetchError) {
      logError(fetchError);
      toaster.failure('Failed to fetch files');
    }

    setLoading(false);
  };

  const uploadFile = async (file: File) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const { tokens } = store.getState();
    const id_token = tokens?.loginTokens?.id_token;

    const url = `${SEARCH_URL}/document_uploads`;

    try {
      const response = await fetch(url, {
        headers: {
          ...(id_token ? { authorization: `Bearer ${id_token}` } : {}),
        },
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload file');
      }

      toaster.success('File uploaded successfully');
      getFiles();
    } catch (error) {
      logError(error);
      toaster.failure('Failed to upload file');
    }

    setUploading(false);
  };

  useEffect(() => {
    getFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { files, loading, getFiles, uploadFile, uploading };
};
